import { useCallback, useEffect, useRef, useState } from 'react';

import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';

let navigationGuardArmed = true;

/**
 * Call before any programmatic navigation (window.location.href, reload, etc.)
 * to prevent the navigation guard from showing a confirmation dialog.
 */
export function disarmNavigationGuard() {
  navigationGuardArmed = false;
}

/**
 * Blocks in-app navigation (browser back/forward via popstate) and shows the
 * browser's native "Leave site?" dialog on tab close / refresh when
 * {@link shouldBlock} is true.
 *
 * Returns `{ isBlocked, stay, leave }` to drive a confirmation modal.
 *
 * Fires analytics to measure interruption rates and user decisions
 * (OKR: Smooth Session Rate > 90%).
 */
export function useNavigationGuard(shouldBlock: boolean) {
  const [isBlocked, setIsBlocked] = useState(false);
  const { userViewed } = useTrackEvent();
  const isProcessingRef = useRef(false);

  useEffect(
    function interceptPopstateAndBeforeUnload() {
      if (!shouldBlock) return;

      navigationGuardArmed = true;
      const abortController = new AbortController();
      const { signal } = abortController;

      window.addEventListener(
        'beforeunload',
        (event: BeforeUnloadEvent) => {
          if (!navigationGuardArmed) return;

          event.preventDefault();
          userViewed(EventTypeEnum.NAVIGATION_GUARD_TRIGGERED, {
            navigationGuardTrigger: 'beforeunload',
          });
        },
        { signal },
      );

      window.addEventListener(
        'popstate',
        () => {
          if (isProcessingRef.current || !navigationGuardArmed) return;

          window.history.pushState(null, '', window.location.href);

          setIsBlocked(true);
          userViewed(EventTypeEnum.NAVIGATION_GUARD_TRIGGERED, {
            navigationGuardTrigger: 'in_app',
          });
        },
        { signal },
      );

      window.history.pushState(null, '', window.location.href);

      return () => abortController.abort();
    },
    [shouldBlock, userViewed],
  );

  const stay = useCallback(() => {
    userViewed(EventTypeEnum.NAVIGATION_GUARD_TRIGGERED, {
      navigationGuardTrigger: 'in_app',
      navigationGuardOutcome: 'stay',
    });
    setIsBlocked(false);
  }, [userViewed]);

  const leave = useCallback(() => {
    userViewed(EventTypeEnum.NAVIGATION_GUARD_TRIGGERED, {
      navigationGuardTrigger: 'in_app',
      navigationGuardOutcome: 'leave',
    });
    setIsBlocked(false);
    isProcessingRef.current = true;
    window.history.back();
  }, [userViewed]);

  return { isBlocked, stay, leave };
}
